import type { ReactElement } from "react";

// Own renderer instead of misc/Markdown (marked): gendered words like "Bürger*innen und Anwohner*innen"
// would turn into emphasis there, and [ZU PRÜFEN] markers need their highlight.
const isTableRow = (t: string) => /^\s*\|.*\|\s*$/.test(t);
const isDividerRow = (t: string) => /^\s*\|[\s:|-]+\|\s*$/.test(t);
const cells = (t: string) =>
  t
    .trim()
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((c) => c.trim());

/** Minimal markdown for the fixed output format: headings, lists, pipe tables, bold. */
export const DraftMarkdown = ({ text }: { text: string }) => {
  const lines = text.split("\n");
  const blocks: ReactElement[] = [];
  let list: string[] = [];
  let ordered = false;
  let table: string[] = [];

  const closeList = (key: number) => {
    if (list.length === 0) return;
    const items = list.map((l, i) => (
      <li key={i} className="ml-5 list-outside">
        <Inline text={l} />
      </li>
    ));
    const cls = "my-3 space-y-1 text-sm leading-relaxed";
    blocks.push(
      ordered ? (
        <ol key={`l${key}`} className={`list-decimal ${cls}`}>
          {items}
        </ol>
      ) : (
        <ul key={`l${key}`} className={`list-disc ${cls}`}>
          {items}
        </ul>
      ),
    );
    list = [];
  };

  const closeTable = (key: number) => {
    if (table.length === 0) return;
    const [head, ...rows] = table.filter((t) => !isDividerRow(t)).map(cells);
    blocks.push(
      <div key={`t${key}`} className="my-4 overflow-x-auto">
        <table className="w-full overflow-hidden rounded-lg border text-sm">
          <thead>
            <tr className="bg-muted text-left">
              {head.map((c, j) => (
                <th
                  key={j}
                  className="border-b px-3 py-2 align-top font-semibold"
                >
                  <Inline text={c} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri} className="border-b align-top last:border-0">
                {r.map((c, j) => (
                  <td key={j} className="px-3 py-2">
                    <Inline text={c} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>,
    );
    table = [];
  };

  lines.forEach((raw, i) => {
    const l = raw.trimEnd();
    if (isTableRow(l)) {
      closeList(i);
      table.push(l);
      return;
    }
    closeTable(i);

    const ul = l.match(/^[-*]\s+(.*)$/);
    const ol = l.match(/^\d+[.)]\s+(.*)$/);
    if (ul || ol) {
      if (!!ol !== ordered && list.length > 0) closeList(i);
      ordered = !!ol;
      list.push((ul?.[1] ?? ol?.[1]) as string);
      return;
    }
    closeList(i);

    if (l.startsWith("# ")) {
      blocks.push(
        <h1 key={i} className="mb-3 mt-2 text-xl font-semibold">
          {l.slice(2)}
        </h1>,
      );
    } else if (l.startsWith("## ")) {
      blocks.push(
        <h2 key={i} className="mb-2 mt-6 text-base font-semibold text-primary">
          {l.slice(3)}
        </h2>,
      );
    } else if (l.startsWith("### ")) {
      blocks.push(
        <h3 key={i} className="mb-1 mt-4 text-sm font-semibold">
          {l.slice(4)}
        </h3>,
      );
    } else if (l.trim() === "") {
      blocks.push(<div key={i} className="h-2" />);
    } else {
      blocks.push(
        <p key={i} className="my-2 text-sm leading-relaxed">
          <Inline text={l} />
        </p>,
      );
    }
  });
  closeList(lines.length);
  closeTable(lines.length + 1);

  return <article className="max-w-3xl">{blocks}</article>;
};

/** Bold text and [ZU PRÜFEN] markers. */
const Inline = ({ text }: { text: string }) => {
  // Models escape asterisks ("Fragesteller\*innen"); the text shows the plain asterisk.
  const parts = text
    .replace(/\\\*/g, "*")
    .split(/(\*\*.+?\*\*|\[ZU PRÜFEN:[^\]]*\])/g);
  return (
    <>
      {parts.map((t, i) => {
        if (t.startsWith("**") && t.endsWith("**"))
          return <strong key={i}>{t.slice(2, -2)}</strong>;
        if (t.startsWith("[ZU PRÜFEN"))
          return (
            <mark
              key={i}
              className="rounded bg-destructive-soft px-1 text-destructive"
            >
              {t}
            </mark>
          );
        return <span key={i}>{t}</span>;
      })}
    </>
  );
};
